import { LandingFooter, LandingNav } from './LandingPage'
import { useSession } from '../lib/session'

/**
 * The rights holders page: what somebody who holds rights in a newspaper, or
 * appears in one, should send and what happens next.
 *
 * It says only what the platform does. The publisher holds the rights and
 * decides; the platform records the outcome and never retouches a page. A
 * promise about response time is a business promise, not a property of the
 * software, so none is printed here.
 */
export function RightsPage() {
  const { config } = useSession()
  const mail = `mailto:${config?.support_email ?? ''}?subject=${encodeURIComponent('Rights request')}`
  return (
    <div className="min-h-full bg-paper text-body">
      <LandingNav />
      <div className="mx-auto max-w-[1200px] px-[--edge]">
        <section className="pb-[calc(2*var(--leading))] pt-[calc(3*var(--leading))]">
          <div className="mb-[--half] text-[13px] uppercase leading-[--half] tracking-[0.08em] text-ink2">
            Rights holders
          </div>
          <h1 className="max-w-[24ch] text-[clamp(34px,4.6vw,64px)] leading-[1.04] tracking-[-0.018em]">
            The publisher holds the rights. The platform holds the record.
          </h1>
          <p className="mt-[--leading] max-w-[58ch] text-[17px] leading-[--leading] text-ink2">
            Every newspaper here is served under a licence from its publisher to the institutions
            that subscribe. If you hold rights in something published here, or appear in it, and
            believe it should not be online or is described wrongly, write to us. The request goes
            to the publisher, who decides.
          </p>
          <div className="mt-[--leading] flex flex-wrap gap-3">
            <a href={mail} className="btn-primary no-underline">
              Write to {config?.support_email ?? 'the platform'}
            </a>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-[calc(1.5*var(--leading))] gap-x-[clamp(28px,4vw,64px)] border-t border-ink pb-[calc(2.5*var(--leading))] pt-[calc(1.5*var(--leading))] md:grid-cols-3">
          <div>
            <h2 className="text-[24px] leading-[--leading] tracking-[-0.01em]">What to send</h2>
            <p className="mt-[--half] text-[15.5px] leading-[--leading] text-ink2">
              The publication and date, the page, and which item on it: a headline, a photograph,
              an advertisement or a notice. A permalink copied from the viewer carries all of that.
              Say what the claim rests on, rights, privacy or an error in the record, and how to
              reach you.
            </p>
          </div>
          <div>
            <h2 className="text-[24px] leading-[--leading] tracking-[-0.01em]">What happens</h2>
            <p className="mt-[--half] text-[15.5px] leading-[--leading] text-ink2">
              The request is passed to the publisher, who holds the rights and makes the decision.
              The platform administrator carries the decision out, and every administrative action
              on the archive is written to an audit log that the publisher can be shown.
            </p>
          </div>
          <div>
            <h2 className="text-[24px] leading-[--leading] tracking-[-0.01em]">What never happens</h2>
            <p className="mt-[--half] text-[15.5px] leading-[--leading] text-ink2">
              A page is never retouched. The text as printed is kept as it was read, and a
              correction is recorded beside it, not over it. An item the publisher withdraws is
              taken out of view; the record of what the paper printed is not rewritten.
            </p>
          </div>
        </section>

        <LandingFooter />
      </div>
    </div>
  )
}
